import { useEffect } from "react"
import { Loader2, RotateCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAdminStore, type OrganizationHealth } from "@/stores/admin-store"

interface OrgHealthPanelProps {
  organizationId: string
}

type OrgFunnel = OrganizationHealth["funnel"]

/**
 * Workflow activity counters. These are NOT narrowing funnel stages — the six
 * values are counted in three different units (events, distinct bids, approved
 * plan rows), so a later label can legitimately exceed an earlier one. Render
 * them as a plain label + count list; never as a chart or stage percentages.
 */
const FUNNEL_LABELS: Array<{ key: keyof OrgFunnel; label: string }> = [
  { key: "documentUploaded", label: "Documents uploaded" },
  { key: "requirementsGenerated", label: "Tenders analysed" },
  { key: "planApproved", label: "Plans approved" },
  { key: "draftCompleted", label: "Drafts completed" },
  { key: "reviewApproved", label: "Reviews approved" },
  { key: "artifactExported", label: "Exports" },
]

type OrgAiActivity = OrganizationHealth["aiActivity"]

// AI usage volume — how much the org asks the AI to do, distinct from the
// workflow outcomes above. Plans/drafts count completed agent turns, so a
// regenerated draft counts every time; that is the point of the number.
const AI_ACTIVITY_LABELS: Array<{ key: keyof OrgAiActivity; label: string }> = [
  { key: "plansGenerated", label: "Plans generated" },
  { key: "draftsGenerated", label: "Drafts generated" },
  { key: "goNoGoScoresApplied", label: "Go/No-Go AI scores applied" },
]

export function OrgHealthPanel({ organizationId }: OrgHealthPanelProps) {
  const health = useAdminStore((s) => s.orgHealth[organizationId])
  const loading = useAdminStore((s) => s.orgHealthLoading === organizationId)
  const loadError = useAdminStore((s) => s.orgHealthError[organizationId])
  const fetchOrgHealth = useAdminStore((s) => s.fetchOrgHealth)

  useEffect(() => {
    // Fetch only when nothing is known and nothing is in flight:
    // - `!loadError` keeps a failure operator-driven (no auto-retry loop)
    // - `!loading` stops Retry double-firing — the action's first set() clears
    //   the error synchronously, which would otherwise re-open this branch
    //   while the operator's own request is still running
    if (!health && !loadError && !loading) void fetchOrgHealth(organizationId)
  }, [organizationId, health, loadError, loading, fetchOrgHealth])

  if (loading) {
    return (
      <div role="status" className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading health…
      </div>
    )
  }

  // A failed fetch must never read as "no activity" — this panel is a
  // monitoring drill-down, and silence would be taken as a real signal.
  if (!health && loadError) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <span>Couldn't load health data.</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void fetchOrgHealth(organizationId)}
        >
          <RotateCw className="h-4 w-4 mr-1" />
          Retry
        </Button>
      </div>
    )
  }

  if (!health) return null

  const idle = health.activeDays === 0

  return (
    <div className="py-4">
      <p className="text-sm font-medium">Health — last 30 days</p>

      {idle ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No activity in the last 30 days.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            Active {health.activeDays} day{health.activeDays === 1 ? "" : "s"} ·{" "}
            {health.activeUsers} user{health.activeUsers === 1 ? "" : "s"} ·{" "}
            {health.bidsInFlight} bid{health.bidsInFlight === 1 ? "" : "s"} in flight ·{" "}
            {health.bidsSubmitted} submitted
          </p>

          <ol className="mt-3 divide-y divide-border">
            {FUNNEL_LABELS.map(({ key, label }) => (
              <li key={key} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium">{health.funnel[key]}</span>
              </li>
            ))}
          </ol>

          <p className="mt-6 text-sm font-medium">AI activity</p>
          <ol className="mt-1 divide-y divide-border">
            {AI_ACTIVITY_LABELS.map(({ key, label }) => (
              <li key={key} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium">{health.aiActivity[key]}</span>
              </li>
            ))}
          </ol>

          <p className="mt-3 text-meta">
            Feedback: {health.feedback.positive} positive, {health.feedback.negative} negative ·{" "}
            Agent spend: ${health.agentSpendUsd.toFixed(2)}
          </p>
        </>
      )}
    </div>
  )
}
